/**
 * The single status → tone map (P-UI-005, STAT-001..016).
 *
 * Every status shown anywhere in Fruuit Engage goes through `statusTone()`, so a value always
 * looks the same on every screen. The map is explicit (STAT-006): no regex matching, and
 * unknown values fall back to `quiet`. Keys are normalised (case, underscores, spacing), so
 * `in_review`, `In review` and `IN REVIEW` resolve to the same entry.
 */

export type Tone = 'positive' | 'attention' | 'watch' | 'quiet' | 'info'

export const TONES: readonly Tone[] = ['positive', 'attention', 'watch', 'quiet', 'info']

/** Grouped only for readability; lookups use the flattened, normalised map below. */
const TONE_GROUPS: Record<string, Record<string, Tone>> = {
  // STAT-001..004: published norms findings
  finding: {
    'No material concern identified': 'positive',
    'Further review required': 'watch',
    'Substantiated norms concern': 'attention',
    'Insufficient information': 'watch',
    'Not yet reviewed': 'watch',
    'Research not yet released': 'watch',
  },
  // constants.json PRIORITIES
  priority: {
    'Routine monitoring': 'quiet',
    'Watchlist / further review': 'watch',
    'Active research / engagement': 'info',
    'Urgent review': 'attention',
  },
  // STAT-013: engagement stages (with and without the "Sn " prefix)
  stage: {
    'S0 Baseline': 'quiet',
    'S1 Dialogue established': 'quiet',
    'S2 Commitment recorded': 'info',
    'S3 Implementation evidenced': 'info',
    'S4 Outcome evidenced': 'positive',
    Baseline: 'quiet',
    'Dialogue established': 'quiet',
    'Commitment recorded': 'info',
    'Implementation evidenced': 'info',
    'Outcome evidenced': 'positive',
  },
  // Case status (STAT-014) and derived labels
  case: {
    active: 'quiet',
    stalled: 'attention',
    closed: 'quiet',
    'not engaged': 'quiet',
    'next action': 'info',
    overdue: 'attention',
  },
  // STAT-016: reviewed-record and minutes workflow (P-WF-001..004)
  review: {
    draft: 'quiet',
    in_review: 'info',
    approved: 'info',
    published: 'positive',
    returned: 'watch',
    retracted: 'attention',
    superseded: 'quiet',
    archived: 'quiet',
    'not released': 'quiet',
  },
  // R-11 / P-PART-001: participation requests
  participation: {
    requested: 'info',
    acknowledged: 'info',
    company_consent_pending: 'watch',
    confirmed: 'positive',
    declined: 'quiet',
    withdrawn: 'quiet',
  },
  // Meetings (CAL-*) and their display labels
  meeting: {
    cancelled: 'quiet',
    'agenda published': 'positive',
    'agenda available': 'positive',
    'team draft': 'watch',
    'reviewed minutes available': 'positive',
  },
  // STAT-009 mapping states
  mapping: {
    verified: 'positive',
    'needs review': 'watch',
    unresolved: 'watch',
    'out of scope': 'quiet',
    'unresolved issuer': 'watch',
    'mapping needs review': 'watch',
    'mapping unresolved': 'watch',
  },
  // STAT-010 review freshness; search-run states
  freshness: {
    'Source check partial': 'watch',
    'Source check failed': 'attention',
    'Review overdue': 'attention',
    'Within recorded scope': 'positive',
    'Source review not completed': 'watch',
    complete: 'positive',
    partial: 'watch',
    failed: 'attention',
  },
  // M10 feed runs (ok/partial/empty/failed/late) and provider status
  feed: {
    ok: 'positive',
    empty: 'quiet',
    late: 'attention',
    running: 'info',
    simulated: 'info',
    'licence pending': 'watch',
    licensed: 'positive',
  },
  // Retrieval-signal triage states (S13)
  triage: {
    new: 'info',
    'mapping review': 'watch',
    'duplicate / linked': 'quiet',
    'relevant new incident': 'attention',
    'relevant update': 'info',
    'outside scope': 'quiet',
    irrelevant: 'quiet',
    dismissed: 'quiet',
    promoted: 'positive',
  },
  // Questions (P-ENG-001)
  question: {
    'awaiting response': 'watch',
    'manager response': 'positive',
    answered: 'positive',
    open: 'info',
  },
  // Alerts
  severity: {
    info: 'info',
    low: 'quiet',
    medium: 'watch',
    warning: 'watch',
    high: 'attention',
    critical: 'attention',
  },
  // Users, orgs, invites, subscriptions (admin)
  account: {
    invited: 'info',
    deactivated: 'quiet',
    suspended: 'attention',
    expired: 'quiet',
    revoked: 'quiet',
    granted: 'positive',
  },
}

export function normalizeStatus(value: string): string {
  return value.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ')
}

const TONE_MAP: ReadonlyMap<string, Tone> = (() => {
  const map = new Map<string, Tone>()
  for (const group of Object.values(TONE_GROUPS)) {
    for (const [key, tone] of Object.entries(group)) {
      const normal = normalizeStatus(key)
      const existing = map.get(normal)
      if (existing && existing !== tone) {
        throw new Error(`status "${key}" is mapped to two tones (${existing}, ${tone})`)
      }
      map.set(normal, tone)
    }
  }
  return map
})()

/** Tone for any status value. Unknown, empty or missing values are `quiet` (STAT-006). */
export function statusTone(value: string | null | undefined): Tone {
  if (!value) return 'quiet'
  return TONE_MAP.get(normalizeStatus(value)) ?? 'quiet'
}

/** True when the value is in the map (useful for tests and dev warnings). */
export function isKnownStatus(value: string): boolean {
  return TONE_MAP.has(normalizeStatus(value))
}

const LABELS: Record<string, string> = {
  'in review': 'In review',
  'company consent pending': 'Company consent pending',
  ok: 'OK',
}

/**
 * Display label (STAT-016): raw workflow values such as `in_review` become "In review",
 * and stage prefixes are dropped ("S3 Implementation evidenced" → "Implementation evidenced").
 * Values that already read as prose are returned unchanged.
 */
export function statusLabel(value: string): string {
  const trimmed = value.trim()
  const stage = /^S[0-4] (.+)$/.exec(trimmed)
  if (stage?.[1]) return stage[1]
  const known = LABELS[normalizeStatus(trimmed)]
  if (known) return known
  if (/[A-Z]/.test(trimmed)) return trimmed
  const words = trimmed.replace(/_+/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}
